import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import localize from "../../../common/localize";
import { getValueInCurrency, getPaymentTypeModeLabel } from "../../../common/utils";
import { BackForeColor } from "../../../components/common/BackForeColor";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import {
  getCurrencyFromSetup,
  getSettlementStatusName,
  getSettlementStatusBackColor,
  getSettlementStatusForeColor,
} from "../../../common/EntitiesManagement";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : localize.no_data;

const renderPersonal = (personal) => {
  if (!personal) return <ValueOrNoDataLabel data={undefined} />;
  const name = personal.FullName;
  const idPart = [personal.IdentificationType, personal.Identification]
    .filter(Boolean)
    .join(": ");
  const text = idPart ? `${name} (${idPart})` : name;
  return (
    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
      {text || localize.no_data}
    </Typography>
  );
};

const renderConcept = (source) => {
  if (!source?.Name) return <ValueOrNoDataLabel data={undefined} />;
  const isPercentage = source.Type === 1;
  const typeName = isPercentage
    ? localize.percentage_value
    : localize.fixed_value;
  const value = isPercentage
    ? `${source.DefaultValue ?? 0}%`
    : getValueInCurrency(getCurrencyFromSetup(), source.DefaultValue ?? 0);
  return (
    <ValueOrNoDataLabel data={`${source.Name} (${typeName}: ${value})`} />
  );
};

const renderAdditionalCostConcept = (act) => {
  if (!act?.Name) return <ValueOrNoDataLabel data={undefined} />;
  const modeLabel = getPaymentTypeModeLabel(act.PaymentMode);
  const base = modeLabel ? `${act.Name} (${modeLabel})` : act.Name;
  const pt = act.paymentType;
  if (!pt?.Name) {
    return <ValueOrNoDataLabel data={base} />;
  }
  const isPercentage = pt.Type === 1;
  const typeName = isPercentage
    ? localize.percentage_value
    : localize.fixed_value;
  const value = isPercentage
    ? `${pt.DefaultValue ?? 0}%`
    : getValueInCurrency(getCurrencyFromSetup(), pt.DefaultValue ?? 0);
  return (
    <ValueOrNoDataLabel data={`${base} / ${pt.Name} (${typeName}: ${value})`} />
  );
};

export default function SettlementDetailCard({
  item,
  readOnly = false,
  hideEventCode = false,
  onStatusClick,
  onEditClick,
}) {
  const event = item?.event ?? {};
  const eventType = event?.EventType ?? {};
  const value = item?.value ?? 0;
  const valueColor = value < 0 ? "#C62828" : value > 0 ? "#2E7D32" : "#10366E";

  const statusClickable = !readOnly && item?.Status === 0 && !!onStatusClick;
  const showEditButton = !readOnly && item?.Status === 0 && !!onEditClick;

  return (
    <Card
      key={item?.id ?? item?.Uuid}
      id={`sd-${item?.id ?? item?.Uuid}`}
      variant="outlined"
      sx={{ width: "100%", flexShrink: 0 }}
    >
      <CardContent className="flex flex-col gap-2">
        <Box className="flex flex-row flex-wrap items-center justify-between gap-2">
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: valueColor }}>
            {getValueInCurrency(getCurrencyFromSetup(), value)}
          </Typography>
          <Box className="flex flex-row flex-wrap items-center gap-2">
            {eventType?.Name && (
              <BackForeColor
                styleName="text-xs my-0"
                backColor={eventType?.BackgroundColor}
                foreColor={eventType?.ForegroundColor}
                text={eventType?.Name}
              />
            )}
            <Box
              onClick={statusClickable ? () => onStatusClick(item) : undefined}
              sx={{ cursor: statusClickable ? "pointer" : "default" }}
            >
              <BackForeColor
                styleName="text-xs my-0"
                backColor={getSettlementStatusBackColor(item?.Status)}
                foreColor={getSettlementStatusForeColor(item?.Status)}
                text={getSettlementStatusName(item?.Status)}
                TooltipText={
                  statusClickable
                    ? localize.settlement_change_status_tooltip
                    : undefined
                }
              />
            </Box>
            {(item?.id ?? item?.Uuid) && (
              <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                {(item?.id ?? item?.Uuid).split("-")[0]}
              </Typography>
            )}
            {showEditButton && (
              <Tooltip title={localize.settlement_edit_tooltip} arrow>
                <IconButton
                  aria-label="edit"
                  onClick={() => onEditClick(item)}
                  sx={{ color: "#10366E", p: 0.25 }}
                >
                  <Edit sx={{ width: 16, height: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider />

        <Box className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.settlement_base_value}
            </Typography>
            <ValueOrNoDataLabel
              data={getValueInCurrency(
                getCurrencyFromSetup(),
                item?.baseValue ?? 0,
              )}
            />
          </Box>
          {!hideEventCode && (
            <Box>
              <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                {localize.service_order}
              </Typography>
              {event?.Code && event?.Id ? (
                <a
                  href={`/events/detail/${event.Id}?tab_id=15`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {event.Code}
                </a>
              ) : (
                <ValueOrNoDataLabel data={event?.Code} />
              )}
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.department}
            </Typography>
            <ValueOrNoDataLabel data={event?.State?.Name} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.city}
            </Typography>
            <ValueOrNoDataLabel data={event?.City?.Name} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.personal}
            </Typography>
            {renderPersonal(item?.personal)}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.settlement_origin}
            </Typography>
            <ValueOrNoDataLabel
              data={
                Number(item?.settlementType) === 2
                  ? localize.settlement_origin_additional_cost
                  : localize.settlement_origin_event_type
              }
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.settlement_concept}
            </Typography>
            {Number(item?.settlementType) === 2
              ? renderAdditionalCostConcept(item?.additionalCostType)
              : renderConcept(item?.paymentType)}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.modification_date}
            </Typography>
            <ValueOrNoDataLabel data={formatDate(item?.ModificationDate)} />
          </Box>
          {item?.StatusChangedBy && (
            <Box>
              <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                {localize.settlement_status_changed_by}
              </Typography>
              <ValueOrNoDataLabel data={item?.StatusChangedBy} />
            </Box>
          )}
        </Box>

        {item?.Observations && (
          <Alert severity="info" className="w-full">
            <Box className="text-xs">
              <b>{`${localize.observations}:`}</b>
              <Divider className="w-full" sx={{ my: 0.5 }} />
              <span style={{ whiteSpace: "pre-line" }}>
                {item.Observations}
              </span>
            </Box>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
